/**
 * web/src/screens/Notebook/hooks/filters.ts — the filters/search seam of
 * useNotebookState (card vncVrGk1hc9i, round two): the links-view kind /
 * subtype / status controls and collection search box, the per-card
 * disclosures, and the feed keyboard walk. Pure move out of the former
 * hooks.ts — no behaviour change.
 */
import { useState } from 'react';
import type { NotebookContext } from './context';

export function useNotebookFilters({ contextLive, contextId, setContextId, setFeedRoving }: {
  contextLive: NotebookContext['contextLive'];
  contextId: NotebookContext['contextId'];
  setContextId: NotebookContext['setContextId'];
  setFeedRoving: NotebookContext['setFeedRoving'];
}) {
  /* ── The links view (card LcJbm9wsWa0B, PRD §5 layer 1/2 + §6.7) ──────────
   * The category dropdown is the top-level index and its order is the founder's
   * (NOTEBOOK_LINK_KINDS), so the options render straight off the routing table
   * rather than off a hand-kept list that could drift. Subtype tabs appear only
   * once a kind is chosen; the status control and search narrow the same set.
   * Card vHyQrgOMJyPg: the chip ROW is gone — one search, one Filters button
   * whose panel carries every category, subtype and lifecycle control. */
  const [kindFilter, setKindFilter] = useState<string | 'all'>('all');
  const [subtypeFilter, setSubtypeFilter] = useState<string | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'saved' | 'planned' | 'done'>('all');
  const [search, setSearch] = useState('');
  /* Card UVRx4xesTEig — typing in the collection search refetches the list
     with `q` (debounced, so a keystroke is not a request). The match itself —
     text AND tag — is the server's WHERE (GET /api/notebook?q=…), not a
     client-side filter: the list is paginated, so a page the client never
     received cannot be filtered into existence. The query is only sent while
     the links view owns the search box (Everything / Links), so a leftover
     query on a single-collection tab still cannot empty Memories, Notes or
     Ideas (card xUZFWf8JtHo2 item 3). */

  /* Sol review 2026-10-19 (card xNGOA5rZz9LV): the subtype and lifecycle
     controls moved behind one Filters button so a default visit shows the
     cards, not the furniture. The count on the button is the number of things
     currently narrowing the list, so a filter that narrows silently is
     impossible — and Clear all puts the view back in one click. */
  const [filtersOpen, setFiltersOpen] = useState(false);
  /* Sol review 2026-10-19, card xNGOA5rZz9LV phase 2 — the calm card face.
     Per-card Details disclosure, plus a separate opener for the suggestions so
     a pending scan still announces itself on the face ("N suggested tags")
     without the editors landing on every visit. Both key on itemId, so one
     card open never opens its neighbours. */
  const [detailsOpen, setDetailsOpen] = useState<ReadonlySet<string>>(new Set());
  const [suggsOpen, setSuggsOpen] = useState<ReadonlySet<string>>(new Set());
  const toggleId = (set: ReadonlySet<string>, id: string): ReadonlySet<string> => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };
  /* Card dwJftHgMiSJm (founder email 2026-10-28, extended to phones 2026-10-30:
     "exists on mobile as well"). Opening a card's Details near the foot of the
     feed used to put the panel — and the filing select inside it — below the
     fold on every width, so the thing you asked for arrived off-screen and the
     list read as stuck. After the open paints, bring the panel's tail into
     view: 'nearest' means a card with room never moves, and the CSS
     scroll-margin on .nb-details keeps the fixed tab bar off the last row. */
  const openDetails = (itemId: string) => {
    const willOpen = !detailsOpen.has(itemId);
    setDetailsOpen(s => toggleId(s, itemId));
    if (!willOpen) return;
    requestAnimationFrame(() => {
      const card = document.querySelector(`[data-context-card="${itemId}"]`);
      const panel = card?.querySelector('[data-test="nb-details"]');
      panel?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  };
  const activeFilterCount =
    (kindFilter !== 'all' ? 1 : 0) +
    (subtypeFilter !== 'all' ? 1 : 0) +
    (statusFilter !== 'all' ? 1 : 0) +
    (search.trim() ? 1 : 0);
  const clearFilters = () => {
    setKindFilter('all');
    setSubtypeFilter('all');
    setStatusFilter('all');
    setSearch('');
  };

  /* ── PRD §8 keyboard: the feed is one tab stop, arrows walk it ─────────────
     Arrow Down/Up (Left/Right too, for grid view) move between cards, Enter
     opens the focused card in the context panel and Escape closes it. The
     handler bails unless the CARD itself is the event target, so the caret
     still walks text in the edit composer, the selects still step their values
     and the capture bar's own Escape (C4) keeps meaning "clear the field". */
  const feedKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const card = target.closest<HTMLElement>('[data-context-card]');
    if (!card || target !== card) return;
    const cards = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-context-card]')];
    const i = cards.indexOf(card);
    if (i < 0) return;
    const moveTo = (next: number) => {
      const el = cards[next];
      if (!el) return;
      e.preventDefault();
      setFeedRoving(el.dataset.contextCard ?? null);
      el.focus();
    };
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') moveTo(i + 1);
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') moveTo(i - 1);
    else if (e.key === 'Enter') {
      if (!contextLive) return;
      const id = card.dataset.contextCard ?? null;
      if (!id) return;
      e.preventDefault();
      setContextId(cur => (cur === id ? null : id));
    } else if (e.key === 'Escape') {
      if (!contextId) return;
      e.preventDefault();
      setContextId(null);
    }
  };

  return {
    kindFilter, setKindFilter, subtypeFilter, setSubtypeFilter, statusFilter, setStatusFilter,
    search, setSearch, filtersOpen, setFiltersOpen,
    detailsOpen, setDetailsOpen, suggsOpen, setSuggsOpen,
    toggleId, openDetails, activeFilterCount, clearFilters, feedKeyDown,
  };
}

export type NotebookFilters = ReturnType<typeof useNotebookFilters>;
